import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Radio, Map, FileText, Wind, Compass, DollarSign, AlertTriangle, X, ClipboardList, ExternalLink, QrCode } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
export function PilotInfo() {
  const {
    t
  } = useLanguage();
  const tabs = [{
    id: 'frequencies',
    label: t.pilot.tabs.frequencies,
    icon: Radio
  }, {
    id: 'circuits',
    label: t.pilot.tabs.circuits,
    icon: Compass
  }, {
    id: 'fees',
    label: t.pilot.tabs.fees,
    icon: DollarSign
  }, {
    id: 'arrival-form',
    label: t.pilot.tabs.arrivalForm,
    icon: ClipboardList,
    externalUrl: 'https://forms.gle/jREeEspwa3jHGTkF9'
  }, {
    id: 'info',
    label: t.pilot.tabs.info,
    icon: FileText
  }];
  const [activeTab, setActiveTab] = useState('frequencies');
  const [lightboxImage, setLightboxImage] = useState<string | null>(null);
  useEffect(() => {
    if (lightboxImage) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [lightboxImage]);
  const handleTabClick = (tab: (typeof tabs)[number]) => {
    if ('externalUrl' in tab && tab.externalUrl) {
      window.open(tab.externalUrl, '_blank', 'noopener,noreferrer');
    } else {
      setActiveTab(tab.id);
    }
  };
  return <section id="pilot-info" className="py-24 bg-[#0a1628]" data-id="element-197" data-mp="28vzyw7qs6tt5">
      <div className="max-w-5xl mx-auto px-6" data-id="element-198" data-mp="2du1lh2nf1b9d">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center mb-12" data-id="element-199" data-mp="2dt8go780ovtv">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-200" data-mp="3t9qxe8lea3vh">
            {t.pilot.title}
          </h2>
          <p className="text-white/60 max-w-2xl mx-auto mb-6" data-id="element-201" data-mp="1u4h2kbr0x0fp">
            {t.pilot.subtitle}
          </p>
          <a href="https://aim.rlp.cz/vfrmanual/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-6 py-3 text-sm font-medium text-[#38bdf8] border-2 border-[#38bdf8] rounded-none hover:bg-[#38bdf8] hover:text-[#0a1628] transition-all duration-300" data-id="element-202" data-mp="1p6nwrjtmu8fy">
            <ExternalLink size={16} data-id="element-203" data-mp="16gzg8vqj2uk4" />
            {t.pilot.vfrManual}
          </a>
        </motion.div>

        {/* Tabs Navigation */}
        <div className="flex flex-wrap justify-center gap-2 mb-8" data-id="element-204" data-mp="1k8mbcs8qhx1b">
          {tabs.map(tab => {
          const isExternal = 'externalUrl' in tab && tab.externalUrl;
          return <button key={tab.id} onClick={() => handleTabClick(tab)} className={`flex items-center gap-2 px-6 py-3 rounded-full text-sm font-medium transition-all duration-300 ${isExternal ? 'bg-[#f87171] text-white hover:bg-[#ef4444]' : activeTab === tab.id ? 'bg-[#38bdf8] text-[#0a1628]' : 'bg-[#0f1d35] text-white/60 hover:bg-[#1e3a5f] hover:text-white'}`} data-id="element-205" data-mp="rq8o1ml0xtgh">
                <tab.icon size={16} data-id="element-206" data-mp="2pd14x0an1p8c" />
                {tab.label}
                {isExternal && <ExternalLink size={12} className="ml-0.5 opacity-60" data-id="element-207" data-mp="2uc4care3c7jh" />}
              </button>;
        })}
        </div>

        {/* Tab Content */}
        <div className="bg-[#0f1d35] border border-white/5 rounded-2xl p-8 md:p-12 min-h-[400px]" data-id="element-208" data-mp="3m4uzkcdsqlwl">
          <AnimatePresence mode="wait" data-id="element-209" data-mp="3853mcdv3t9jn">
            {activeTab === 'frequencies' && <motion.div key="frequencies" initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -10
          }} transition={{
            duration: 0.3
          }} data-id="element-210" data-mp="3d3ew6olefk1v">
                <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-3" data-id="element-211" data-mp="286mn8qiaqxnf">
                  <Radio className="text-[#38bdf8]" data-id="element-212" data-mp="2381nes6yifzi" /> {t.pilot.freq.title}
                </h3>

                {/* Desktop Table */}
                <div className="hidden md:block overflow-hidden rounded-xl border border-white/10 mb-8" data-id="element-213" data-mp="2zx0y0dv0jtuv">
                  <table className="w-full text-left" data-id="element-214" data-mp="2vxs4na3vxxzu">
                    <thead className="bg-[#0a1628]" data-id="element-215" data-mp="2qx42pte4bwqu">
                      <tr data-id="element-216" data-mp="2qvt0hy0bmx1u">
                        <th className="p-4 text-white/50 font-medium text-sm uppercase tracking-wider" data-id="element-217" data-mp="2qz74faomvebe">
                          {t.pilot.freq.station}
                        </th>
                        <th className="p-4 text-white/50 font-medium text-sm uppercase tracking-wider" data-id="element-218" data-mp="1fjigoh3k2b9j">
                          {t.pilot.freq.callsign}
                        </th>
                        <th className="p-4 text-white/50 font-medium text-sm uppercase tracking-wider" data-id="element-219" data-mp="10gqrde3p0lo1">
                          {t.pilot.freq.frequency}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5" data-id="element-220" data-mp="1abiqwngu5ilg">
                      <tr className="bg-[#0f1d35]" data-id="element-221" data-mp="1f5mucwqw78hs">
                        <td className="p-4 text-white font-medium" data-id="element-222" data-mp="1j5xa314hld9u">RADIO</td>
                        <td className="p-4 text-white/70" data-id="element-223" data-mp="1j81cfemqm480">HOŘOVICE RADIO</td>
                        <td className="p-4 text-[#38bdf8] font-mono font-bold text-lg" data-id="element-224" data-mp="1o2x9it6tzhrz">
                          135.580 MHz
                        </td>
                      </tr>
                      <tr className="bg-[#0f1d35]" data-id="element-225" data-mp="1y1mu4tq43gbx">
                        <td className="p-4 text-white font-medium" data-id="element-226" data-mp="3uvhip7bqg6py">
                          {t.pilot.freq.emergency}
                        </td>
                        <td className="p-4 text-white/70" data-id="element-227" data-mp="3pgg7n5u2lfx">
                          {t.pilot.freq.general}
                        </td>
                        <td className="p-4 text-red-400 font-mono font-bold text-lg" data-id="element-228" data-mp="3rvfywa1j980y">
                          121.500 MHz
                        </td>
                      </tr>
                      <tr className="bg-[#0f1d35]" data-id="element-229" data-mp="5npte00ty2rq">
                        <td className="p-4 text-white font-medium" data-id="element-230" data-mp="n26nqjmw83b">ATIS</td>
                        <td className="p-4 text-white/70" data-id="element-231" data-mp="en34ex4lqzek">
                          {t.pilot.freq.prague}
                        </td>
                        <td className="p-4 text-white/40 font-mono" data-id="element-232" data-mp="9myrti6teotm">
                          122.160 MHz
                        </td>
                      </tr>
                      <tr className="bg-[#0f1d35]" data-id="element-233" data-mp="jdtg0s3mfyhp">
                        <td className="p-4 text-white font-medium" data-id="element-234" data-mp="jebiw7kshayl">VOLMET</td>
                        <td className="p-4 text-white/70" data-id="element-235" data-mp="xemajctxatkt">-</td>
                        <td className="p-4 text-white/40 font-mono" data-id="element-236" data-mp="tbzfz45vaujx">
                          125.525 MHz
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Mobile Cards */}
                <div className="md:hidden space-y-3 mb-8" data-id="element-237" data-mp="1gxa1jb4sv4vu">
                  <div className="bg-[#0a1628] rounded-xl border border-white/5 p-4" data-id="element-238" data-mp="12yjt4zcj5is6">
                    <div className="flex justify-between items-start mb-1" data-id="element-239" data-mp="17xwqie112890">
                      <span className="text-white font-medium" data-id="element-240" data-mp="t9s2cuj7h4iu">RADIO</span>
                      <span className="text-[#38bdf8] font-mono font-bold" data-id="element-241" data-mp="t7p6vuzaaj6q">
                        135.580 MHz
                      </span>
                    </div>
                    <span className="text-white/50 text-sm" data-id="element-242" data-mp="kaeto42wojz6">
                      HOŘOVICE RADIO
                    </span>
                  </div>
                  <div className="bg-[#0a1628] rounded-xl border border-white/5 p-4" data-id="element-243" data-mp="aaxpxs77sg5u">
                    <div className="flex justify-between items-start mb-1" data-id="element-244" data-mp="ad0myw6sb9k2">
                      <span className="text-white font-medium" data-id="element-245" data-mp="f9hfs3oxic3h">
                        {t.pilot.freq.emergency}
                      </span>
                      <span className="text-red-400 font-mono font-bold" data-id="element-246" data-mp="psxlhl0vx04l">
                        121.500 MHz
                      </span>
                    </div>
                    <span className="text-white/50 text-sm" data-id="element-247" data-mp="lpiwi4uyx9f0">
                      {t.pilot.freq.general}
                    </span>
                  </div>
                  <div className="bg-[#0a1628] rounded-xl border border-white/5 p-4" data-id="element-248" data-mp="btfur0413csl">
                    <div className="flex justify-between items-start mb-1" data-id="element-249" data-mp="72pm9e0b2tto">
                      <span className="text-white font-medium" data-id="element-250" data-mp="70mog7swvwee">ATIS</span>
                      <span className="text-white/50 font-mono" data-id="element-251" data-mp="23mjy1bpn1pe">
                        122.160 MHz
                      </span>
                    </div>
                    <span className="text-white/50 text-sm" data-id="element-252" data-mp="3p8m0258sk5ev">
                      {t.pilot.freq.prague}
                    </span>
                  </div>
                  <div className="bg-[#0a1628] rounded-xl border border-white/5 p-4" data-id="element-253" data-mp="3kb2iavcvxai6">
                    <div className="flex justify-between items-start mb-1" data-id="element-254" data-mp="1zqp23yrtkr29">
                      <span className="text-white font-medium" data-id="element-255" data-mp="24oqjmf9lkibm">VOLMET</span>
                      <span className="text-white/50 font-mono" data-id="element-256" data-mp="29old8qkrbm0d">
                        125.525 MHz
                      </span>
                    </div>
                    <span className="text-white/50 text-sm" data-id="element-257" data-mp="2dolz2wa6qzhg">-</span>
                  </div>
                </div>

                <div className="p-4 bg-[#38bdf8]/10 border border-[#38bdf8]/20 rounded-lg" data-id="element-258" data-mp="1gymgsa48gmlo">
                  <p className="text-[#38bdf8] text-sm" data-id="element-259" data-mp="1lx64aeyh66g5">
                    <strong data-id="element-260" data-mp="1q0c71cmdpfhv">{t.pilot.freq.noteLabel}</strong>{' '}
                    {t.pilot.freq.note}
                  </p>
                </div>
              </motion.div>}

            {activeTab === 'circuits' && <motion.div key="circuits" initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -10
          }} transition={{
            duration: 0.3
          }} data-id="element-261" data-mp="y7v9rf7px8ag">
                <div className="grid md:grid-cols-2 gap-8" data-id="element-262" data-mp="3ag5azzvbjw3i">
                  <div data-id="element-263" data-mp="2dqxpgrup9mzl">
                    <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-3" data-id="element-264" data-mp="2drh4o0sytx9x">
                      <Wind className="text-[#38bdf8]" data-id="element-265" data-mp="2hs145b10hcz8" /> {t.pilot.runway.title}
                    </h3>
                    <div className="bg-[#0a1628] p-6 rounded-xl border border-white/5 mb-6" data-id="element-266" data-mp="2mrdonp4eu7yn">
                      <div className="flex justify-between items-center mb-4" data-id="element-267" data-mp="2rivsqm1qconu">
                        <span className="text-xl font-bold text-white" data-id="element-268" data-mp="1u2yfhj7ymjv8">
                          RWY 06 / 24
                        </span>
                        <span className="px-3 py-1 bg-[#38bdf8]/20 text-[#38bdf8] rounded text-sm font-bold" data-id="element-269" data-mp="1z0qfl0irdcai">
                          {t.pilot.runway.surface}
                        </span>
                      </div>
                      <div className="space-y-2 text-sm text-white/70" data-id="element-270" data-mp="1mk8lp01msxh1">
                        <p data-id="element-271" data-mp="1hjb6mwbx4jjr">
                          {t.pilot.runway.dimensions}:{' '}
                          <strong data-id="element-272" data-mp="1djirg0s0rf5f">1170 x 30 m</strong>
                        </p>
                        <p data-id="element-273" data-mp="18jecwgao8uhu">
                          {t.pilot.runway.strength}:{' '}
                          <strong data-id="element-274" data-mp="13sxz2v2xv6uy">PCN 25/R/B/U</strong>
                        </p>
                        <p data-id="element-275" data-mp="13quyjh45tvbz">
                          {t.pilot.runway.slope}: <strong data-id="element-276" data-mp="yuweu6ts11hw">0.18%</strong>{' '}
                          {t.pilot.runway.slopeNote}
                        </p>
                        <p className="text-yellow-400/80" data-id="element-277" data-mp="twl3frnu2ixk">
                          {t.pilot.runway.warning}
                        </p>
                      </div>
                    </div>

                    {/* Runway Diagram Thumbnail */}
                    <div className="relative overflow-hidden rounded-xl border border-white/10 cursor-pointer group" onClick={() => setLightboxImage('https://cdn.magicpatterns.com/uploads/u3egShru5eatQbgsvmYZ6f/lkhv_adc.webp')} data-id="element-278" data-mp="b5y7xnzjzb2c">
                      <img src="https://cdn.magicpatterns.com/uploads/u3egShru5eatQbgsvmYZ6f/lkhv_adc.webp" alt={t.pilot.runway.mapAlt} className="w-full h-auto object-contain bg-white/90 p-2 transition-transform duration-500 group-hover:scale-105" data-id="element-279" data-mp="tx7kk64mefy5" />
                      <div className="absolute inset-0 bg-[#0a1628]/40 transition-opacity duration-300 group-hover:opacity-0" data-id="element-280" data-mp="p4o491ejt6pn" />
                    </div>
                  </div>

                  <div data-id="element-281" data-mp="1hnlczxr4agt9">
                    <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-3" data-id="element-282" data-mp="1ho4iug3hteqx">
                      <Compass className="text-[#38bdf8]" data-id="element-283" data-mp="32be21ff9jd25" />{' '}
                      {t.pilot.circuits.title}
                    </h3>
                    <ul className="space-y-4" data-id="element-284" data-mp="3b596el8k8zgq">
                      <li className="flex items-start gap-3 text-white/80" data-id="element-285" data-mp="3b3z4tm2i195c">
                        <span className="w-2 h-2 bg-[#38bdf8] rounded-full mt-2" data-id="element-286" data-mp="3735k11kcsago" />
                        <span data-id="element-287" data-mp="2jdt9nydahwcs">
                          <strong data-id="element-288" data-mp="2jf4bvtr36w1s">{t.pilot.circuits.northOnly}</strong>{' '}
                          {t.pilot.circuits.northOnlyRest}
                          <br data-id="element-289" data-mp="2eh2vxhoyjcuj" />
                          <span className="text-sm text-white/50" data-id="element-290" data-mp="2sej4ozuq7ipg">
                            {t.pilot.circuits.turnNote}
                          </span>
                        </span>
                      </li>
                      <li className="flex items-start gap-3 text-white/80" data-id="element-291" data-mp="7o0ok1du1zm0">
                        <span className="w-2 h-2 bg-[#38bdf8] rounded-full mt-2" data-id="element-292" data-mp="2ph2m0z8ontn" />
                        <span data-id="element-293" data-mp="2rjuyrnv6t1j">
                          {t.pilot.circuits.altitudeLabel}{' '}
                          <strong className="text-white" data-id="element-294" data-mp="y1qen158ex62">2 300 ft AMSL</strong>{' '}
                          (QNH)
                          <br data-id="element-295" data-mp="17xtchut1w9n7" />
                          <span className="text-sm text-white/50" data-id="element-296" data-mp="17xjvgwpzhctn">
                            {t.pilot.circuits.altitudeNote}
                          </span>
                        </span>
                      </li>
                    </ul>
                  </div>
                </div>

                <div className="mt-8 p-6 bg-red-500/10 border border-red-500/20 rounded-xl" data-id="element-297" data-mp="1lx2satdch1qn">
                  <h4 className="text-red-400 font-bold flex items-center gap-2 mb-2" data-id="element-298" data-mp="1qokwdqanzifu">
                    <AlertTriangle size={20} data-id="element-299" data-mp="1vne75aykgvcb" /> {t.pilot.noise.title}
                  </h4>
                  <ul className="list-disc list-inside text-white/70 space-y-1 text-sm" data-id="element-300" data-mp="1vp8mm9r2jv90">
                    {t.pilot.noise.items.map(item => <li key={item} data-id="element-301" data-mp="24p3x0ccsvm7z">{item}</li>)}
                  </ul>
                </div>
              </motion.div>}

            {activeTab === 'fees' && <motion.div key="fees" initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -10
          }} transition={{
            duration: 0.3
          }} data-id="element-302" data-mp="3l3gi7xkpi7i7">
                <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-3" data-id="element-303" data-mp="7rzev9zd8eyt">
                  <DollarSign className="text-[#38bdf8]" data-id="element-304" data-mp="2wk10i1cyj2e" /> {t.pilot.fees.title}
                </h3>

                <div className="grid md:grid-cols-2 gap-6" data-id="element-305" data-mp="3u3dccepv7ykn">
                  <div className="bg-[#0a1628] border border-white/5 rounded-xl overflow-hidden" data-id="element-306" data-mp="vfzcmtv3h5mw">
                    <div className="p-4 bg-white/5 border-b border-white/5 font-bold text-white" data-id="element-307" data-mp="qnphmzf4hfdr">
                      {t.pilot.fees.landing}
                    </div>
                    <div className="p-4 space-y-4" data-id="element-308" data-mp="2oyg4lv1cf7hf">
                      <div className="flex justify-between items-center border-b border-white/5 pb-2" data-id="element-309" data-mp="2ox55w9mr8px6">
                        <span className="text-white/70" data-id="element-310" data-mp="2b4fh5xfud6wd">
                          {t.pilot.fees.ultralight}
                        </span>
                        <span className="text-[#38bdf8] font-bold" data-id="element-311" data-mp="217u8n24m5dns">120 Kč</span>
                      </div>
                      <div className="flex justify-between items-center border-b border-white/5 pb-2" data-id="element-312" data-mp="267p0b7w7q79w">
                        <span className="text-white/70" data-id="element-313" data-mp="1s8fmufze5r9m">
                          {t.pilot.fees.upTo2000}
                        </span>
                        <span className="text-[#38bdf8] font-bold" data-id="element-314" data-mp="1x70liayzw2vy">150 Kč</span>
                      </div>
                      <div className="flex justify-between items-center" data-id="element-315" data-mp="3mjlwkw0zg59y">
                        <span className="text-white/70" data-id="element-316" data-mp="3rk9n7glauk3n">
                          {t.pilot.fees.over2000}
                        </span>
                        <span className="text-[#38bdf8] font-bold" data-id="element-317" data-mp="5gyb9b500ud9">
                          270 Kč{' '}
                          <span className="text-xs text-white/40 font-normal" data-id="element-318" data-mp="ebmcmq7gdrws">
                            + 140 Kč/t
                          </span>
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-[#0a1628] border border-white/5 rounded-xl overflow-hidden h-min" data-id="element-319" data-mp="o88x3dvvagya">
                    <div className="p-4 bg-white/5 border-b border-white/5 font-bold text-white" data-id="element-320" data-mp="jah4sirb8stk">
                      {t.pilot.fees.other}
                    </div>
                    <div className="p-4 space-y-4" data-id="element-321" data-mp="t8n2eo633csm">
                      <div className="flex justify-between items-center border-b border-white/5 pb-2" data-id="element-322" data-mp="t72bd8izabzi">
                        <span className="text-white/70" data-id="element-323" data-mp="2z3oehusrue9e">
                          {t.pilot.fees.parking}
                        </span>
                        <span className="text-[#38bdf8] font-bold" data-id="element-324" data-mp="3jlzs1w7ekzbl">140 Kč</span>
                      </div>
                      <div className="flex justify-between items-center" data-id="element-325" data-mp="3oj9fx7f592xb">
                        <span className="text-white/70" data-id="element-326" data-mp="3taho604qmi8w">
                          {t.pilot.fees.unauthorized}
                        </span>
                        <span className="text-red-400 font-bold" data-id="element-327" data-mp="16p876lalm1q">
                          {t.pilot.fees.from} 10 000 Kč
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* QR payment codes */}
                <div className="mt-8" data-id="element-328" data-mp="39m8ybwqo2ock">
                  <h4 className="text-sm font-medium text-white/50 uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-329" data-mp="39pd8daqtceb5">
                    <QrCode size={16} className="text-[#38bdf8]" data-id="element-330" data-mp="oxb45zi0vfvd" />
                    {t.pilot.fees.qrTitle}
                  </h4>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-331" data-mp="tul07xzx2ps6">
                    {[{
                  label: t.pilot.fees.ultralight,
                  amount: '120 Kč',
                  src: 'https://cdn.magicpatterns.com/uploads/gY1rmtrMsUsHmCsyFXWVU5/600.jpg'
                }, {
                  label: t.pilot.fees.upTo2000,
                  amount: '150 Kč',
                  src: 'https://cdn.magicpatterns.com/uploads/8o7h339DLw15vwz6yv3GJy/2000.jpg'
                }, {
                  label: t.pilot.fees.over2000,
                  amount: '270 Kč',
                  src: 'https://cdn.magicpatterns.com/uploads/aA2wjYtqT9n9AxTHhUYvsP/2001.jpg'
                }, {
                  label: t.pilot.fees.parking,
                  amount: '140 Kč',
                  src: 'https://cdn.magicpatterns.com/uploads/vBUwhbirA3VC2kVHcDCdtG/parking.jpg'
                }].map(qr => <div key={qr.label} className="flex flex-col bg-[#0a1628] border border-white/5 rounded-xl p-4" data-id="element-332" data-mp="8bh3l2t37467">
                        <div className="bg-white rounded-lg p-2 mb-3" data-id="element-333" data-mp="3vkm7iwypsyfo">
                          <img src={qr.src} alt={`QR ${qr.label} — ${qr.amount}`} className="w-full h-auto" data-id="element-334" data-mp="3qmayupk5otz1" />
                        </div>
                        <span className="text-white text-xs leading-snug mb-1" data-id="element-335" data-mp="3grixrbrd7ozi">
                          {qr.label}
                        </span>
                        <span className="text-[#38bdf8] font-bold text-sm mt-auto" data-id="element-336" data-mp="37pjfyh8j0gvi">
                          {qr.amount}
                        </span>
                      </div>)}
                  </div>
                </div>

                <p className="mt-6 text-sm text-white/50 text-center" data-id="element-337" data-mp="15vagmdtx62fr">
                  {t.pilot.fees.note}
                </p>
              </motion.div>}

            {activeTab === 'info' && <motion.div key="info" initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -10
          }} transition={{
            duration: 0.3
          }} data-id="element-338" data-mp="1n6h0fblm0p2h">
                <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-3" data-id="element-339" data-mp="14f6aawnpkgbn">
                  <FileText className="text-[#38bdf8]" data-id="element-340" data-mp="18ichc6inabj6" /> {t.pilot.info.title}
                </h3>

                <div className="space-y-6" data-id="element-341" data-mp="2fucus9npi4w3">
                  <div className="bg-[#0a1628] p-6 rounded-xl border border-white/5" data-id="element-342" data-mp="2fxhao4ajap2l">
                    <h4 className="text-white font-bold mb-2" data-id="element-343" data-mp="2jyszw8phx8a9">
                      {t.pilot.info.atzTitle}
                    </h4>
                    <p className="text-white/70 text-sm mb-2" data-id="element-344" data-mp="fteoklu7va49">
                      {t.pilot.info.atzHorizontal}{' '}
                      <strong data-id="element-345" data-mp="trldvdl3o1sb">3 NM (5,5 km)</strong> {t.pilot.info.atzFromArp}
                      <br data-id="element-346" data-mp="ot1rjc2lgnzf" />
                      {t.pilot.info.atzVertical}{' '}
                      <strong data-id="element-347" data-mp="13z1bftw9mtb">4 000 ft AMSL</strong> (1200 m).
                    </p>
                    <p className="text-white/50 text-xs" data-id="element-348" data-mp="11mkj5zvhihu">
                      {t.pilot.info.atzNote}
                    </p>
                  </div>

                  <div className="bg-[#0a1628] p-6 rounded-xl border border-white/5" data-id="element-349" data-mp="autyhf8oi6tj">
                    <h4 className="text-white font-bold mb-2" data-id="element-350" data-mp="62tsp65xgjp1">
                      {t.pilot.info.alternatesTitle}
                    </h4>
                    <ul className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm" data-id="element-351" data-mp="3eh2ffxlrqg9a">
                      <li className="text-white/70" data-id="element-352" data-mp="39j0wrds98iyc">
                        <strong className="text-white" data-id="element-353" data-mp="1p2049pba8yt9">Příbram (LKPM)</strong>
                        <br data-id="element-354" data-mp="1ozx2y94ojj98" />
                        118.755 MHz, 20 km
                      </li>
                      <li className="text-white/70" data-id="element-355" data-mp="1tub3epy78gkr">
                        <strong className="text-white" data-id="element-356" data-mp="1g24m30jb83jq">Beroun (LKBU)</strong>
                        <br data-id="element-357" data-mp="1kzn035c6p8yl" />
                        134.305 MHz, 25 km
                      </li>
                      <li className="text-white/70" data-id="element-358" data-mp="28p7k7rk2qc9g">
                        <strong className="text-white" data-id="element-359" data-mp="28qiortlcfo1m">Kladno (LKKL)</strong>
                        <br data-id="element-360" data-mp="2cq19ao995x3x" />
                        123.480 MHz, 30 km
                      </li>
                      <li className="text-white/70" data-id="element-361" data-mp="1yqrqrhxkqqws">
                        <strong className="text-white" data-id="element-362" data-mp="23qe2to1o2140">Rakovník (LKRA)</strong>
                        <br data-id="element-363" data-mp="23sh2z0wj9amg" />
                        123.510 MHz, 30 km
                      </li>
                    </ul>
                  </div>

                  <div className="bg-[#0a1628] p-6 rounded-xl border border-white/5" data-id="element-364" data-mp="sciouimhzyed">
                    <h4 className="text-white font-bold mb-2" data-id="element-365" data-mp="17lnp99ts04sz">
                      {t.pilot.info.altimeterTitle}
                    </h4>
                    <p className="text-white/70 text-sm" data-id="element-366" data-mp="12sukwn27yyhf">
                      {t.pilot.info.altimeterText}{' '}
                      <strong data-id="element-367" data-mp="yqpxi62d6y38">381 m (1 249 ft) AMSL</strong>
                    </p>
                  </div>
                </div>
              </motion.div>}
          </AnimatePresence>
        </div>
      </div>

      {/* Lightbox for runway diagram */}
      <AnimatePresence data-id="element-368" data-mp="1mjg748msp5ac">
        {lightboxImage && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.3
      }} className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm p-4 md:p-8" onClick={() => setLightboxImage(null)} data-id="element-369" data-mp="1hjbood2bu0hh">
            <button className="absolute top-6 right-6 text-white/70 hover:text-white transition-colors z-50 bg-black/20 p-2 rounded-full backdrop-blur-md" onClick={e => {
          e.stopPropagation();
          setLightboxImage(null);
        }} data-id="element-370" data-mp="3n82n79196szu">
              <X size={32} data-id="element-371" data-mp="5rcbvwiabrpf" />
            </button>
            <motion.div initial={{
          scale: 0.9,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} exit={{
          scale: 0.9,
          opacity: 0
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 300
        }} className="relative max-w-5xl max-h-[90vh] w-full flex items-center justify-center" onClick={e => e.stopPropagation()} data-id="element-372" data-mp="9s4lwryze0mr">
              <img src={lightboxImage} alt={t.pilot.runway.mapAlt} className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl bg-white p-2" data-id="element-373" data-mp="2ierfwlfh7mtn" />
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </section>;
}